import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@ordering/utils/styling';

const pillVariants = cva('inline-flex items-center rounded-full border', {
  variants: {
    variant: {
      warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      info: 'bg-blue-100 text-blue-800 border-blue-200',
      success: 'bg-green-100 text-green-800 border-green-200',
      error: 'bg-red-100 text-red-800 border-red-200',
      default: 'bg-gray-100 text-gray-800 border-gray-200',
      black: 'bg-black text-white border-black',
      branded: 'bg-brand-primary-background text-brand-primary-foreground border-brand-primary-background',
    },
    size: {
      xs: 'px-2 py-0.5 text-[10px] leading-none',
      sm: 'px-2 py-1 text-xs',
      md: 'px-3 py-1 text-sm',
      lg: 'px-4 py-2 text-base',
    },
    weight: {
      regular: 'font-regular',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
    weight: 'medium',
  },
});

export interface PillProps extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof pillVariants> {
  asChild?: boolean;
  as?: React.ElementType;
}

const Pill = React.forwardRef<HTMLSpanElement, PillProps>(
  ({ className, variant, size, weight, asChild = false, as = 'span', children, ...props }, ref) => {
    const Comp = asChild ? Slot : as;

    return (
      <Comp className={cn(pillVariants({ variant, size, weight, className }))} ref={ref} {...props}>
        {children}
      </Comp>
    );
  }
);

Pill.displayName = 'Pill';

export { Pill, pillVariants };
